import React, { useState, useMemo, useCallback } from 'react';
import { dateFr } from '../services/dates';
import ChampDate from './ChampDate';
import './StagePublic.css';

/**
 * SUIVI DE STAGE — la FICHE DE VISITE du tuteur, étape ⑧ (lot 3.5c)
 * ==================================================================
 * Spec : doc/modules/suivi_de_stage.md, « Page tuteur — fiche de visite », écarts E146, E155 → E162
 * Maquette : l'aperçu « Fiche de visite (vue tuteur) » — `#psc-apercu-visite` : en-tête vert, encadré
 * étudiant / entreprise / période, date de visite, un bloc `.eval-critere` par critère (niveaux en pastilles,
 * commentaire), le total, le commentaire général, « Valider la fiche ».
 *
 * ── CE QUE LA SPEC AJOUTE À LA MAQUETTE ────────────────────────────────────
 *   · le MODE de visite (radios stylés présentiel / téléphonique / visio, obligatoire) et la DURÉE
 *     (30 min / 1 h / 1 h 30 / 2 h / Autre, facultative) ;
 *   · « Remplie par » (E156) : prérempli avec le tuteur par le lien, imposé (le compte) par « Mes visites » ;
 *   · la LECTURE après enregistrement (« Enregistrée le … — transmise au département ») et « Modifier la
 *     fiche » APRÈS CONFIRMATION (E157) — la version précédente est gardée par le serveur ;
 *   · le bandeau orange « Visite à effectuer avant le … » tant qu'aucune fiche n'est enregistrée.
 *
 * ── UNE PAGE, TROIS USAGES ──────────────────────────────────────────────────
 * le lien du tuteur (`enregistrer` / `recharger` = l'API publique), l'aperçu de l'éditeur de modèles (E116 :
 * l'aperçu EST la page, `apercu` n'envoie rien) et « Mes visites » (lot 3.5c-4, porte compte).
 *
 * ⚠️ Les niveaux et les modes sont de VRAIS boutons radio habillés : clavier et doigt marchent tels quels.
 * ⚠️ La fiche affichée est la COPIE FIGÉE de la campagne (ou celle de la fiche enregistrée) : c'est le
 * serveur qui l'envoie, et qui recontrôle tout.
 */

const pts = (n) => `${n} pt${n > 1 ? 's' : ''}`;
const MODES_DEFAUT = [{ cle: 'presentiel', libelle: 'En présentiel' }, { cle: 'telephonique', libelle: 'Téléphonique' }, { cle: 'visio', libelle: 'Visio' }];
const ICONE_MODE = { presentiel: 'fa-building', telephonique: 'fa-phone', visio: 'fa-video' };
const DUREES_DEFAUT = [30, 60, 90, 120];
const dureeLib = (m) => {
    if (!m) return null;
    const h = Math.floor(m / 60), r = m % 60;
    return h ? `${h} h${r ? ' ' + String(r).padStart(2, '0') : ''}` : `${m} min`;
};
const JOUR_PARIS = new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' });
const heureFr = (v) => (v ? new Date(v).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '');

function Message({ ton, icone, titre, children }) {
    return (
        <div className="pub-page">
            <div className={'pub-message' + (ton ? ' pub-message-' + ton : '')}>
                <i className={'fa-solid ' + icone} />
                <h2>{titre}</h2>
                {children}
            </div>
        </div>
    );
}

/** La saisie de départ : la fiche courante (pour la relire ou la modifier), sinon une fiche vierge. */
function saisieDepuis(page, remplieParImposee, durees) {
    const c = page.courante;
    if (!c) {
        return { date: '', mode: '', duree: '', dureeAutre: '', niveaux: {}, commentaires: {}, commentaire: '',
                 remplie: remplieParImposee || page.remplie_par_defaut || '' };
    }
    const connue = durees.includes(c.duree_minutes);
    return {
        date: c.date_visite || '', mode: c.mode_visite || '',
        duree: c.duree_minutes ? (connue ? String(c.duree_minutes) : 'autre') : '',
        dureeAutre: c.duree_minutes && !connue ? String(c.duree_minutes) : '',
        niveaux: { ...(c.niveaux || {}) }, commentaires: { ...(c.commentaires || {}) }, commentaire: c.commentaire || '',
        remplie: remplieParImposee || c.remplie_par || ''
    };
}

function StageVisitePublique({ page: pageInitiale, apercu = false, enregistrer, recharger, remplieParImposee = null, dansVolet = false }) {
    const [page, setPage] = useState(pageInitiale);
    const durees = page.durees || DUREES_DEFAUT;
    const modes = page.modes || MODES_DEFAUT;
    const [edition, setEdition] = useState(!pageInitiale.courante);
    const [s, setS] = useState(() => saisieDepuis(pageInitiale, remplieParImposee, durees));
    const [confirmer, setConfirmer] = useState(false);
    const [enCours, setEnCours] = useState(false);
    const [erreur, setErreur] = useState(null);
    const [succes, setSucces] = useState(null);
    const [fin, setFin] = useState(null);
    const poser = (cle, v) => setS((x) => ({ ...x, [cle]: v }));

    const d = page.dossier || {};
    const fiche = page.fiche || (page.courante ? null : { criteres: [] });
    const criteres = (fiche || {}).criteres || [];
    const masquer = (fiche || {}).masquer_points === true;
    const lecture = !edition;

    const choisi = useCallback((c) => c.niveaux.find((n) => String(n.id) === String(s.niveaux[c.id])), [s.niveaux]);
    const total = useMemo(() => criteres.reduce((t, c) => t + (choisi(c)?.points || 0), 0), [criteres, choisi]);
    const duree = s.duree === 'autre' ? s.dureeAutre : s.duree;

    /** Ce qui manque, en toutes lettres — c'est ce que dit le bouton grisé (E69). */
    const manque = useMemo(() => {
        const liste = [];
        if (!s.date) liste.push('la date de la visite');
        if (!s.mode) liste.push('le mode de la visite');
        const sansNiveau = criteres.map((c, i) => (choisi(c) ? null : i + 1)).filter(Boolean);
        if (sansNiveau.length && sansNiveau.length === criteres.length) liste.push('un niveau pour chaque critère');
        else if (sansNiveau.length) liste.push(`un niveau pour ${sansNiveau.length > 1 ? 'les critères' : 'le critère'} ${sansNiveau.join(', ')}`);
        if (s.duree === 'autre' && !(Number.isInteger(Number(s.dureeAutre)) && Number(s.dureeAutre) >= 1 && Number(s.dureeAutre) <= 1440)) {
            liste.push('une durée en minutes (de 1 à 1440)');
        }
        if (!s.remplie.trim()) liste.push('le nom de la personne qui remplit la fiche');
        return liste;
    }, [s, criteres, choisi]);

    const valider = useCallback(async () => {
        setErreur(null); setSucces(null);
        if (apercu) { setSucces('Aperçu : rien n’a été enregistré. Dans la vraie page, la fiche serait enregistrée et transmise au département.'); return; }
        setEnCours(true);
        try {
            const r = await enregistrer({
                date_visite: s.date, mode_visite: s.mode, duree_minutes: duree ? Number(duree) : null,
                niveaux: s.niveaux, commentaires: s.commentaires, commentaire: s.commentaire, remplie_par: s.remplie,
                remplacer: !!page.courante
            });
            const relue = await recharger();
            setPage(relue);
            setS(saisieDepuis(relue, remplieParImposee, durees));
            setEdition(false);
            setSucces(r?.data?.message || (page.courante ? 'La fiche de visite a été modifiée. La version précédente est conservée.'
                                                         : 'La fiche de visite est enregistrée et transmise au département.'));
            window.scrollTo(0, 0);
        } catch (e) {
            if (e.response?.status === 410) {
                setFin({ titre: e.response.data?.error || 'Lien indisponible', texte: e.response.data?.detail || '' });
            } else {
                setErreur(e.response?.data?.error || "L'enregistrement n'a pas abouti. Réessayez dans un instant.");
            }
        } finally { setEnCours(false); }
    }, [apercu, enregistrer, recharger, s, duree, page.courante, remplieParImposee, durees]);

    const annulerModification = () => { setS(saisieDepuis(page, remplieParImposee, durees)); setEdition(false); setErreur(null); };
    const cadre = 'pub-page' + (dansVolet ? ' pub-dans-volet' : '');

    // ── Les états de page ────────────────────────────────────────────────────
    if (fin) {
        return (
            <Message ton="erreur" icone="fa-link-slash" titre={fin.titre}>
                <p>{fin.texte}</p>
            </Message>
        );
    }
    if (!page.commence) {
        return (
            <Message ton="info" icone="fa-car" titre={`Fiche de visite — ${d.prenom || ''} ${d.nom || ''}`.trim()}>
                <p>{d.formation_nom}{d.semestre ? ` · ${d.semestre}` : ''}{d.entreprise_nom ? ` — ${d.entreprise_nom}` : ''}</p>
                {d.date_debut && <p>Stage du {dateFr(d.date_debut)} au {dateFr(d.date_fin)}.</p>}
                <p><b>La fiche de visite s'ouvrira le {dateFr(page.a_partir_du)}, premier jour du stage.</b></p>
                <p className="pub-discret">Gardez ce courriel : ce lien reste le vôtre. {d.departement_nom ? `— ${d.departement_nom}` : ''}</p>
            </Message>
        );
    }
    if (!page.courante && !page.ouvert) {
        return (
            <Message ton="info" icone="fa-car" titre={`Fiche de visite — ${d.prenom || ''} ${d.nom || ''}`.trim()}>
                <p>{page.raison || "La fiche de visite n'est pas ouverte."}</p>
                <p className="pub-discret">Contactez le secrétariat des stages si vous pensez que c'est une erreur.</p>
            </Message>
        );
    }

    const c = page.courante;
    const enTete = (
        <div className="form-public-header">
            <h2><i className="fa-solid fa-car" /> Fiche de visite de stage</h2>
            <p>{d.departement_nom}{d.formation_nom ? ` · ${d.formation_nom}` : ''}{d.semestre ? ` · ${d.semestre}` : ''}</p>
        </div>
    );

    return (
        <div className={cadre}>
            <div className="form-public">
                {enTete}
                <div className="form-public-body">
                    <div className="pub-eval-stagiaire">
                        <div><strong>Étudiant :</strong> {d.nom} {d.prenom}</div>
                        {d.entreprise_nom && <div><strong>Entreprise :</strong> {d.entreprise_nom}{d.entreprise_ville ? ` (${d.entreprise_ville})` : ''}</div>}
                        {d.maitre && <div><strong>Maître de stage :</strong> {d.maitre}</div>}
                        {d.date_debut && <div><strong>Période :</strong> {dateFr(d.date_debut)} → {dateFr(d.date_fin)}</div>}
                        {d.tuteur && <div><strong>Tuteur pédagogique :</strong> {d.tuteur}</div>}
                    </div>

                    {apercu && (
                        <div className="pub-encart pub-encart-info">
                            <i className="fa-solid fa-eye" /> Aperçu — données fictives. Cette page est celle que reçoit le tuteur ; rien n'est enregistré.
                        </div>
                    )}
                    {succes && <div className="pub-encart pub-encart-succes"><i className="fa-solid fa-circle-check" /> {succes}</div>}

                    {/* La fiche enregistrée : qui, quand — et ce qu'on peut encore en faire. */}
                    {c && lecture && (
                        <div className="pub-visite-enregistree">
                            <div>
                                <i className="fa-solid fa-circle-check" /> <b>Enregistrée le {dateFr(c.date_soumission)} à {heureFr(c.date_soumission)}</b>
                                {' '}par {c.remplie_par} — transmise au département.
                            </div>
                            {page.nb_versions_precedentes > 0 && (
                                <div className="pub-discret"><i className="fa-solid fa-clock-rotate-left" /> Fiche modifiée {page.nb_versions_precedentes === 1 ? 'une fois' : `${page.nb_versions_precedentes} fois`} (versions précédentes conservées).</div>
                            )}
                            {!page.ouvert && page.raison && <div className="pub-discret">{page.raison}</div>}
                        </div>
                    )}
                    {c && edition && (
                        <div className="pub-encart pub-encart-info">
                            <i className="fa-solid fa-pen" /> Vous modifiez la fiche enregistrée le {dateFr(c.date_soumission)} : la version actuelle sera conservée.
                        </div>
                    )}
                    {!c && page.echeance_visite && (
                        <div className={'pub-visite-echeance' + (page.en_retard ? ' pub-visite-retard' : '')}>
                            <i className="fa-solid fa-clock" /> Visite à effectuer avant le {dateFr(page.echeance_visite)}{page.en_retard ? ' — échéance dépassée' : ''}.
                        </div>
                    )}
                    {!c && edition && (
                        <p className="pub-aide pub-aide-bloc">Après votre visite, renseignez sa date et son mode, puis choisissez un niveau pour chaque critère. Vous pourrez modifier la fiche ensuite.</p>
                    )}

                    <div className="pub-visite-contexte">
                        <div className="form-group form-group-large">
                            <label htmlFor="visite-date">Date de visite <span className="form-required">*</span></label>
                            <ChampDate id="visite-date" value={s.date} disabled={lecture} min={d.date_debut || undefined}
                                       max={JOUR_PARIS.format(new Date())} onChange={(e) => poser('date', e.target.value)} />
                        </div>
                        <div className="form-group form-group-large">
                            <label htmlFor="visite-duree">Durée</label>
                            {lecture ? (
                                // En lecture, la durée se dit (« 45 min »), elle ne se montre pas en liste « Autre… ».
                                <input id="visite-duree" value={dureeLib(Number(duree)) || '—'} disabled readOnly />
                            ) : (
                                <>
                                    <select id="visite-duree" value={s.duree} onChange={(e) => poser('duree', e.target.value)}>
                                        <option value="">—</option>
                                        {durees.map((m) => <option key={m} value={String(m)}>{dureeLib(m)}</option>)}
                                        <option value="autre">Autre…</option>
                                    </select>
                                    {s.duree === 'autre' && (
                                        <span className="pub-visite-minutes">
                                            <input type="number" min={1} max={1440} step={1} aria-label="Durée en minutes"
                                                   value={s.dureeAutre} onChange={(e) => poser('dureeAutre', e.target.value)} /> min
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <fieldset className="pub-visite-modes" disabled={lecture}>
                        <legend>Mode de visite <span className="form-required">*</span></legend>
                        <div className="eval-niveaux" role="radiogroup" aria-label="Mode de visite">
                            {modes.map((m) => (
                                <label key={m.cle} className={'eval-niveau-btn' + (s.mode === m.cle ? ' selected' : '')}>
                                    <input type="radio" className="pub-sr" name="visite-mode" value={m.cle} checked={s.mode === m.cle}
                                           onChange={() => poser('mode', m.cle)} />
                                    <i className={'fa-solid ' + (ICONE_MODE[m.cle] || 'fa-circle')} /> {m.libelle}
                                </label>
                            ))}
                        </div>
                    </fieldset>

                    {criteres.map((cr, i) => {
                        const n = choisi(cr);
                        return (
                            <fieldset className="eval-critere" key={cr.id} disabled={lecture}>
                                <legend className="eval-critere-title">
                                    <span>{i + 1} — {cr.libelle}</span>
                                    {!masquer && <span className="eval-score">{n ? pts(n.points) : '— pts'}</span>}
                                </legend>
                                {cr.sous_detail && <div className="pub-aide" style={{ marginBottom: 6 }}>{cr.sous_detail}</div>}
                                <div className="eval-niveaux" role="radiogroup" aria-label={cr.libelle}>
                                    {cr.niveaux.map((niv) => (
                                        <label key={niv.id} className={'eval-niveau-btn' + (n?.id === niv.id ? ' selected' : '')}>
                                            <input type="radio" className="pub-sr" name={`visite-crit-${cr.id}`} value={niv.id} checked={n?.id === niv.id}
                                                   onChange={() => setS((x) => ({ ...x, niveaux: { ...x.niveaux, [cr.id]: niv.id } }))} />
                                            {niv.intitule}{!masquer ? ` — ${pts(niv.points)}` : ''}
                                        </label>
                                    ))}
                                </div>
                                {cr.commentaire_actif && (lecture ? (s.commentaires[cr.id] ? <div className="pub-eval-recap-com">« {s.commentaires[cr.id]} »</div> : null) : (
                                    <textarea className="pub-eval-com" placeholder="Commentaire…" maxLength={2000} aria-label={`Commentaire — ${cr.libelle}`}
                                              value={s.commentaires[cr.id] || ''}
                                              onChange={(e) => setS((x) => ({ ...x, commentaires: { ...x.commentaires, [cr.id]: e.target.value } }))} />
                                ))}
                            </fieldset>
                        );
                    })}

                    {!masquer && criteres.length > 0 && (
                        <div className="eval-total">
                            <span className="eval-total-label"><i className="fa-solid fa-calculator" /> Total</span>
                            <span className="eval-total-score">{total} <span className="eval-total-sur">/ {fiche.total_max} pts</span></span>
                        </div>
                    )}

                    <div className="form-group form-group-large" style={{ marginBottom: 14 }}>
                        <label htmlFor="visite-commentaire">Commentaire général</label>
                        {lecture
                            ? <div className="pub-visite-texte">{s.commentaire || <span className="pub-discret">—</span>}</div>
                            : <textarea id="visite-commentaire" placeholder="Observations complémentaires…" maxLength={4000}
                                        value={s.commentaire} onChange={(e) => poser('commentaire', e.target.value)} />}
                    </div>
                    <div className="form-group form-group-large">
                        <label htmlFor="visite-remplie">Remplie par <span className="form-required">*</span></label>
                        <input id="visite-remplie" maxLength={200} autoComplete="name" value={s.remplie}
                               disabled={lecture || !!remplieParImposee} onChange={(e) => poser('remplie', e.target.value)} />
                        {remplieParImposee && !lecture && <div className="pub-aide">Le nom de votre compte.</div>}
                    </div>
                    {erreur && <div className="pub-encart pub-encart-erreur"><i className="fa-solid fa-circle-exclamation" /> {erreur}</div>}
                </div>

                <div className="form-public-footer pub-pied-depot">
                    {lecture ? (
                        page.ouvert && (
                            <button type="button" className="pub-btn pub-btn-secondaire" onClick={() => setConfirmer(true)}>
                                <i className="fa-solid fa-pen" /> Modifier la fiche
                            </button>
                        )
                    ) : (
                        <>
                            {manque.length > 0 && <div className="pub-aide pub-raison">Il manque {manque.join(', ')}.</div>}
                            <div className="pub-visite-boutons">
                                {c && (
                                    <button type="button" className="pub-btn pub-btn-secondaire" onClick={annulerModification} disabled={enCours}>
                                        Annuler les modifications
                                    </button>
                                )}
                                <button type="button" className="pub-btn" disabled={manque.length > 0 || enCours} onClick={valider}>
                                    {enCours ? <><i className="fa-solid fa-circle-notch pub-tourne" /> Enregistrement…</>
                                             : <><i className="fa-solid fa-check" /> {c ? 'Enregistrer les modifications' : 'Valider la fiche'}</>}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {confirmer && (
                <div className="pub-modale-fond" role="dialog" aria-modal="true" aria-labelledby="visite-confirmer-titre">
                    <div className="pub-modale">
                        <h3 id="visite-confirmer-titre"><i className="fa-solid fa-pen" /> Modifier la fiche de visite ?</h3>
                        <p>La fiche enregistrée le {dateFr(c?.date_soumission)} sera remplacée par votre nouvelle saisie.
                           <b> La version actuelle est conservée</b> : le département pourra la relire.</p>
                        <div className="pub-modale-pied">
                            <button type="button" className="pub-btn pub-btn-secondaire" onClick={() => setConfirmer(false)}>Annuler</button>
                            <button type="button" className="pub-btn" onClick={() => { setConfirmer(false); setSucces(null); setEdition(true); }}>
                                <i className="fa-solid fa-pen" /> Modifier
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default StageVisitePublique;
